import {
  OnChanges,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import {Subject} from 'rxjs';
import {filter, switchMap, takeUntil} from 'rxjs/operators';

import {
  IUserGuideActiveStep,
  IUserGuideStep,
} from '../../shared/user-guide.model';
import {UserGuideService} from '../../shared/user-guide.service';
import {UserGuideStore} from '../../shared/user-guide.store';
import {ItemManagerService} from '@src/app/services/itemManager/item-manager.service';
import {Item} from '@src/app/models/Item';

@Component({
  selector: 'app-user-guide-hint',
  templateUrl: './user-guide-hint.component.html',
  styleUrls: ['./user-guide-hint.component.scss'],
})
export class UserGuideHintComponent implements OnChanges, OnInit, OnDestroy {
  @Input() activeStep: IUserGuideActiveStep;

  private destroyer$: Subject<void> = new Subject();
  private elementDisplayed: string;

  hintPosition: IHintPosition = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private userGuideStore: UserGuideStore,
    private userGuideService: UserGuideService,
    private itemManagerService: ItemManagerService
  ) {}

  ngOnChanges(): void {
    this.hintPosition = null;
    this.toggleElements();

    /**
     * If the element is not visible then a scroll is actioned.
     * Give the action a couple seconds to complete.
     */
    const requiredScroll: boolean = this.requiredMapObjectScroll();
    setTimeout(
      () => {
        this.getHintPosition();
        this.cdr.detectChanges();

        if (requiredScroll) {
          this.userGuideService.refreshOverlay$.next();
        }
      },
      requiredScroll ? 2000 : 0
    );
  }

  ngOnInit(): void {
    this.userGuideService.clickedElement$
      .pipe(
        takeUntil(this.destroyer$),
        filter(() => !!this.activeStep.step.actionRequired)
      )
      .subscribe((path: EventTarget[]) => {
        this.checkForAction(path);
      });

    this.itemManagerService.itemAdded$
      .pipe(
        takeUntil(this.destroyer$),
        switchMap((item: Item) => item.getSubject(Item.AFTER_DRAWN_SUBJECT))
      )
      .subscribe(() => {
        if (this.activeStep.step.requiresDrawing) {
          this.nextStep();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.elementDisplayed) {
      this.toggleElVisibility(this.elementDisplayed, 'hidden');
      this.elementDisplayed = null;
    }

    this.destroyer$.next();
    this.destroyer$.unsubscribe();
  }

  cancel(): void {
    this.userGuideStore.clearActiveStep();
  }

  getHintPosition() {
    const coords: DOMRect = this.getCoords();
    const offset: number = 40;

    if (!coords) {
      this.hintPosition = {
        bottom: 60,
        left: 400,
      };

      return;
    }

    this.hintPosition = {left: 0};

    const center = {
      x: coords.width / 2 + coords.left,
      y: coords.height / 2 + coords.top,
    };

    switch (this.activeStep.step.position) {
      case 'right':
        this.hintPosition.left = coords.right + offset;
        this.hintPosition.top = center.y;
        break;

      case 'left':
        this.hintPosition.left = coords.left - offset;
        this.hintPosition.top = center.y;
        break;

      case 'top':
        this.hintPosition.left = center.x;
        this.hintPosition.top = coords.top - offset;
        break;

      case 'bottom':
        this.hintPosition.left = center.x;
        this.hintPosition.top = coords.bottom + offset;
        break;
    }
  }

  get ctaLabel(): string {
    return this.activeStep.hasNext ? 'Next' : 'Finish';
  }

  nextStep(): void {
    if (!this.activeStep.hasNext) {
      this.userGuideStore.clearActiveStep(true, this.activeStep.parentIndex);
      return;
    }

    // Give animations i.e. the map object sidebar half a second to load
    setTimeout(() => {
      this.userGuideStore.setActiveStep(
        this.activeStep.parentIndex,
        this.activeStep.step.index + 1
      );
    }, 500);
  }

  get hintStyle(): iHintStyle {
    if (!this.hintPosition) {
      return null;
    }

    const hintStyle: iHintStyle = {
      top: `${this.hintPosition.top}px`,
      left: `${this.hintPosition.left}px`,
      width: `${this.activeStep.step.width}px` || '100%',
    };

    if (this.hintPosition.bottom) {
      delete hintStyle.top;
      hintStyle.bottom = `${this.hintPosition.bottom}px`;
    }

    return hintStyle;
  }

  private requiredMapObjectScroll(): boolean {
    const scrollbar = Scrollbar.get(
      document.querySelector('.es-navbar-list-inner-wrapper')
    );

    // Unopened map objects sidebar has a width of 15px
    const isScrollbarOpen: boolean = scrollbar.getSize().container.width !== 15;

    if (!isScrollbarOpen) {
      return false;
    }

    const activeEl: IUserGuideStep['element'] = this.activeStep.step.element;
    const el: HTMLElement =
      scrollbar.containerEl.querySelector(activeEl.className) ||
      scrollbar.containerEl.querySelector(`[data-ug="${activeEl.dataAttr}"]`);

    if (!el || scrollbar.isVisible(el)) {
      return false;
    }

    scrollbar.scrollIntoView(el, {onlyScrollIfNeeded: true});
    return true;
  }

  private toggleElements(): void {
    // Hide element before moving on
    if (this.elementDisplayed) {
      this.toggleElVisibility(this.elementDisplayed, 'hidden');
      this.elementDisplayed = null;
    }

    const elementToShow: string = this.activeStep.step.elementToShow;
    if (elementToShow) {
      this.elementDisplayed = elementToShow;
      this.toggleElVisibility(elementToShow, 'visible');
    }
  }

  private toggleElVisibility(el: string, visibility: string): void {
    (document.querySelector(el) as HTMLElement).style.visibility = visibility;
  }

  private checkForAction(path: EventTarget[]) {
    const activeEl = this.activeStep.step.element;
    const isByClassName: boolean = !!activeEl.className;

    (path as HTMLElement[]).forEach((el: HTMLElement) => {
      if (
        (isByClassName &&
          el.classList &&
          el.classList.contains(activeEl.className.substring(1))) ||
        (el.dataset && el.dataset.ug === activeEl.dataAttr)
      ) {
        this.nextStep();
      }
    });
  }

  private getCoords(): DOMRect | null {
    if (!this.activeStep.step.element) {
      return null;
    }

    const el: HTMLElement = this.userGuideService.getElement(
      this.activeStep.step
    );

    if (!el) {
      this.activeStep.step = {
        ...this.activeStep.step,
        element: null,
        position: 'bottom',
        actionRequired: false,
      };
      return null;
    }

    return el.getBoundingClientRect() as DOMRect;
  }
}

interface IHintPosition {
  top?: number;
  bottom?: number;
  left: number;
}

interface iHintStyle {
  top?: string;
  bottom?: string;
  left: string;
  width: string;
}
